import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import * as Api from 'api/gaio'
import * as Actions from 'actions/gaio'
import * as AppActions from 'actions/app'
import * as Selectors from 'selectors'
import { withGaioProjectId } from 'utils/gaio'
import { retriable } from 'utils/sagas'

const pendingProjectSearches = new Map()
const pendingTagSearches = new Map()

const getSearchKey = query => (query || '').trim()

const getPendingSearch = (pendingSearches, key, request) => {
  if (!pendingSearches.has(key)) {
    const pendingRequest = request()

    pendingSearches.set(key, pendingRequest)
    pendingRequest.then(
      () => pendingSearches.delete(key),
      () => pendingSearches.delete(key)
    )
  }

  return pendingSearches.get(key)
}

export const fetchGaioProjects = body => (
  getPendingSearch(
    pendingProjectSearches,
    getSearchKey(body.name),
    () => Api.searchGaioProjects(body)
  )
)

export const fetchGaioTags = body => (
  getPendingSearch(
    pendingTagSearches,
    getSearchKey(body.name),
    () => Api.searchGaioTags(body)
  )
)

export function* searchProjects({ payload }) {
  try {
    const query = payload?.query

    const body = { name: query }

    const results = yield call(fetchGaioProjects, body)

    yield put(Actions.searchProjectsSuccess(results))
  } catch (error) {
    yield put(Actions.searchProjectsError(error))
  }
}

export function* fetchProjectsCount() {
  try {
    const results = yield call(Api.fetchGaioProjectsCount)

    yield put(Actions.fetchProjectsCountSuccess(results))
  } catch (error) {
    yield put(Actions.fetchProjectsCountError(error))
  }
}

export function* searchPrompts() {
  try {
    const projectId = yield select(Selectors.getGaioSelectedProjectId)
    const promptsPage = yield select(Selectors.getGaioPromptsPage)
    const promptsLimit = yield select(Selectors.getGaioPromptsLimit)
    const promptsFilters = yield select(Selectors.getGaioPromptsFilters)

    const deactivated = promptsFilters.get('deactivated')
    const personas = promptsFilters.get('personas')
    const models = promptsFilters.get('models')
    const tags = promptsFilters.get('tags')

    const hasActive = deactivated.includes(false)
    const hasInactive = deactivated.includes(true)

    let disabled = null

    if (hasActive && !hasInactive) {
      disabled = false
    } else if (hasInactive && !hasActive) {
      disabled = true
    }

    const body = {
      project_id: projectId,
      page: promptsPage,
      limit: promptsLimit,
      filters: {
        personas: personas.toJS(),
        models: models.toJS(),
        tags: tags.toJS()
      },
      disabled
    }

    const results = yield call(Api.searchGaioPrompts, body)

    yield put(Actions.searchPromptsSuccess(results))
  } catch (error) {
    yield put(Actions.searchPromptsError(error))
  }
}

export function* fetchPromptsStatistics() {
  try {
    const results = yield call(Api.fetchGaioPromptsStatistics)

    yield put(Actions.fetchPromptsStatisticsSuccess(results))
  } catch (error) {
    yield put(Actions.fetchPromptsStatisticsError(error))
  }
}

export function* fetchPromptOptions() {
  try {
    const projectId = yield select(Selectors.getGaioSelectedProjectId)
    const results = yield call(Api.fetchGaioPromptOptions, { project_id: projectId })

    yield put(Actions.fetchPromptOptionsSuccess(results))
  } catch (error) {
    yield put(Actions.fetchPromptOptionsError(error))
  }
}

export function* searchPrompt({ payload }) {
  try {
    const { promptId, projectId } = payload

    const body = {
      id: promptId,
      project_id: projectId
    }

    const results = yield call(Api.searchGaioPrompts, body)
    const prompt = results.prompts?.[0] || null

    yield put(Actions.searchPromptSuccess(prompt))
  } catch (error) {
    yield put(Actions.searchPromptError(error))
  }
}

export function* searchTags({ payload }) {
  try {
    const query = payload?.query

    const body = { name: query }
    const results = yield call(fetchGaioTags, body)

    yield put(Actions.searchTagsSuccess(results))
  } catch (error) {
    yield put(Actions.searchTagsError(error))
  }
}

export function* fetchSuggestedPrompts({ payload }) {
  try {
    const projectId = yield select(Selectors.getGaioSelectedProjectId)

    const body = {
      project_id: projectId,
      regenerate: payload.regenerate
    }

    const results = yield call(Api.fetchGaioSuggestedPrompts, body)

    yield put(Actions.fetchSuggestedPromptsSuccess(results))
  } catch (error) {
    yield put(Actions.fetchSuggestedPromptsError(error))
  }
}

export function* fetchPromptNews({ payload }) {
  try {
    const promptId = payload?.promptId
    const promptNewsPage = yield select(Selectors.getGaioPromptNewsPage)
    const promptNewsLimit = yield select(Selectors.getGaioPromptNewsLimit)

    const results = yield call(Api.fetchGaioPromptNews, promptId, promptNewsPage, promptNewsLimit)

    yield put(Actions.fetchPromptNewsSuccess(results))
  } catch (error) {
    yield put(Actions.fetchPromptNewsError(error))
  }
}

export function* fetchSourcesTry() {
  let searchBody = yield select(Selectors.getSearchBody)
  const selectedProjectId = yield select(Selectors.getGaioSelectedProjectId)

  searchBody = withGaioProjectId(searchBody, selectedProjectId)

  if (!searchBody.news?.gaio_project_id) {
    yield put(Actions.sourcesRequestSuccess({ sources: [], total: 0 }))

    return
  }

  const result = yield call(Api.searchGaioSources, searchBody)

  yield put(Actions.sourcesRequestSuccess(result))
}

export function* fetchSourcesFail(error) {
  yield put(Actions.sourcesRequestError(error))
  yield put(AppActions.exception(error))
  yield put(AppActions.genericErrorMessage())
}

export function* fetchSources() {
  const isValidSearch = yield select(Selectors.isValidSearch)
  const newsRequestBlocked = yield select(Selectors.getNewsRequestBlocked)

  if (!isValidSearch || newsRequestBlocked) {
    yield put(Actions.sourcesRequestSuccess({ sources: [], total: 0 }))

    return
  }

  yield call(retriable, fetchSourcesTry, fetchSourcesFail)
}

export function* fetchScorecardTry() {
  let searchBody = yield select(Selectors.getSearchBody)
  const selectedProjectId = yield select(Selectors.getGaioSelectedProjectId)

  searchBody = withGaioProjectId(searchBody, selectedProjectId)

  if (!searchBody.news?.gaio_project_id) {
    yield put(Actions.scorecardRequestSuccess([]))

    return
  }

  const result = yield call(Api.fetchGaioScorecard, searchBody)

  yield put(Actions.scorecardRequestSuccess(result))
}

export function* fetchScorecardFail(error) {
  yield put(Actions.scorecardRequestError(error))
  yield put(AppActions.exception(error))
  yield put(AppActions.genericErrorMessage())
}

export function* fetchScorecard() {
  const isValidSearch = yield select(Selectors.isValidSearch)
  const newsRequestBlocked = yield select(Selectors.getNewsRequestBlocked)

  if (!isValidSearch || newsRequestBlocked) {
    yield put(Actions.scorecardRequestSuccess([]))

    return
  }

  yield call(retriable, fetchScorecardTry, fetchScorecardFail)
}

export function* watchSearchProjects() {
  yield takeEvery(Actions.searchProjectsStart, searchProjects)
}

export function* watchFetchProjectsCount() {
  yield takeLatest(Actions.fetchProjectsCountStart, fetchProjectsCount)
}

export function* watchSearchPrompts() {
  yield takeLatest(Actions.searchPromptsStart, searchPrompts)
}

export function* watchFetchPromptsStatistics() {
  yield takeLatest(Actions.fetchPromptsStatisticsStart, fetchPromptsStatistics)
}

export function* watchFetchPromptOptions() {
  yield takeLatest(Actions.fetchPromptOptionsStart, fetchPromptOptions)
}

export function* watchSearchPrompt() {
  yield takeLatest(Actions.searchPromptStart, searchPrompt)
}

export function* watchSearchTags() {
  yield takeLatest(Actions.searchTagsStart, searchTags)
}

export function* watchFetchSuggestedPrompts() {
  yield takeEvery(Actions.fetchSuggestedPromptsStart, fetchSuggestedPrompts)
}

export function* watchFetchPromptNews() {
  yield takeLatest(Actions.fetchPromptNewsStart, fetchPromptNews)
}

export function* watchFetchSources() {
  yield takeLatest(Actions.sourcesRequestStart, fetchSources)
}

export function* watchFetchScorecard() {
  yield takeLatest(Actions.scorecardRequestStart, fetchScorecard)
}

export default function* searchSaga() {
  yield all([
    watchSearchProjects(),
    watchFetchProjectsCount(),
    watchSearchPrompts(),
    watchFetchPromptsStatistics(),
    watchFetchPromptOptions(),
    watchSearchPrompt(),
    watchSearchTags(),
    watchFetchSuggestedPrompts(),
    watchFetchPromptNews(),
    watchFetchSources(),
    watchFetchScorecard()
  ])
}
